/**
 * School Safety Dashboard - Tab 6 of School Admin Dashboard
 *
 * Content moderation oversight for school administrators:
 * - View all moderation alerts for the school
 * - Filter by severity (CRITICAL, HIGH, MEDIUM, LOW)
 * - Filter by status (NEW, REVIEWED, RESOLVED, ESCALATED)
 * - View alert details and conversation context
 * - Mark alerts as reviewed or false positive
 * - Export compliance reports
 *
 * Data Sources:
 * - GET /api/admin/school/safety (session-based - no ID)
 * - student_flagged_content table
 * - moderation_alerts table (if implemented)
 *
 * References:
 * - docs/CONTENT_MODERATION_FEATURE.md
 */

import React, { useState, useEffect } from 'react';
import axios from '../utils/authInterceptor';

// Tappy Brand Colors - Official 2025 Design System
const TappyBrandColors = {
  primary: {
    darkTeal: '#1A3E48',
    teal: '#297A92',
    mediumTeal: '#388899',
    brandBlue: '#76909F',
    brightTeal: '#42909E',
    aqua: '#4D99A5',
    lightAqua: '#5BA3AC',
    skyBlue: '#6DB4CD',
  },
  accent: {
    coral: '#EA6D37',
    gold: '#ECC962',
    brightGold: '#F8C341',
    tan: '#C59F65',
  },
  neutral: {
    darkGray: '#3D4954',
    mediumGray: '#8D7F88',
    lightTeal: '#ABCACA',
    paleGray: '#D5DEDD',
    lightGray: '#ECF3F2',
    veryLight: '#F5F9FA',
    almostWhite: '#FCFDFE',
  },
  semantic: {
    success: '#42909E',
    warning: '#ECC962',
    error: '#EA6D37',
    info: '#6DB4CD',
  }
};

function SchoolSafetyDashboard({ getApiUrl, isDistrictAdminView }) {
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);

  // Helper to get API URL - use prop if provided, otherwise default to session-based
  const buildApiUrl = (endpoint) => {
    if (getApiUrl) {
      return getApiUrl(endpoint);
    }
    return `/api/admin/school/${endpoint}`;
  };

  // Filters
  const [filters, setFilters] = useState({
    severity: '',
    status: '',
    period: '30d'
  });

  useEffect(() => {
    fetchAlerts();
  }, [filters]);

  const fetchAlerts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.severity) params.append('severity', filters.severity);
      if (filters.status) {
        // Map status filter to backend reviewed parameter
        if (filters.status === 'NEW') params.append('reviewed', 'false');
        else if (filters.status === 'REVIEWED') params.append('reviewed', 'true');
      }
      // Convert period to days
      const periodMap = { '7d': 7, '30d': 30, '90d': 90, 'all': 365 };
      params.append('days', periodMap[filters.period] || 30);

      const response = await axios.get(`${buildApiUrl('safety')}?${params}`);
      setAlerts(response.data.alerts || []);
      setStats({
        critical: response.data.summary?.critical_severity || 0,
        high: response.data.summary?.high_severity || 0,
        medium: response.data.summary?.medium_severity || 0,
        low: response.data.summary?.low_severity || 0,
        total: response.data.summary?.total_incidents || 0
      });
      setError(null);
    } catch (err) {
      console.error('Error fetching safety alerts:', err);
      setError(err.response?.data?.error || 'Failed to load safety data');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkReviewed = async (alertId) => {
    try {
      await axios.post(`${buildApiUrl(`safety/${alertId}/review`)}`, {
        reviewed: true,
        false_positive: false
      });
      fetchAlerts(); // Refresh
    } catch (err) {
      console.error('Error marking alert as reviewed:', err);
      alert('Failed to mark alert as reviewed');
    }
  };

  const handleMarkFalsePositive = async (alertId) => {
    try {
      await axios.post(`${buildApiUrl(`safety/${alertId}/review`)}`, {
        reviewed: true,
        false_positive: true
      });
      fetchAlerts(); // Refresh
    } catch (err) {
      console.error('Error marking alert as false positive:', err);
      alert('Failed to mark alert as false positive');
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity?.toUpperCase()) {
      case 'CRITICAL': return { bg: '#fee2e2', text: '#991b1b', border: '#dc2626' };
      case 'HIGH': return { bg: '#fef3c7', text: '#92400e', border: '#f59e0b' };
      case 'MEDIUM': return { bg: '#fef3c7', text: '#92400e', border: '#fbbf24' };
      case 'LOW': return { bg: '#f3f4f6', text: '#374151', border: '#9ca3af' };
      default: return { bg: '#f3f4f6', text: '#6b7280', border: '#d1d5db' };
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'NEW': return { bg: '#fee2e2', text: '#991b1b', label: 'New' };
      case 'REVIEWED': return { bg: '#dbeafe', text: '#1e40af', label: 'Reviewed' };
      case 'RESOLVED': return { bg: '#d1fae5', text: '#065f46', label: 'Resolved' };
      case 'ESCALATED': return { bg: '#fef3c7', text: '#92400e', label: 'Escalated' };
      default: return { bg: '#f3f4f6', text: '#6b7280', label: 'Unknown' };
    }
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);

    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px' }}>
        <div style={{
          width: '48px',
          height: '48px',
          margin: '0 auto 20px',
          borderRadius: '50%',
          backgroundColor: TappyBrandColors.accent.coral + '15',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '24px',
          color: TappyBrandColors.accent.coral
        }}>!</div>
        <h3 style={{ color: TappyBrandColors.accent.coral, marginBottom: '12px', fontWeight: 600 }}>Error Loading Safety Data</h3>
        <p style={{ color: TappyBrandColors.neutral.mediumGray, marginBottom: '20px' }}>{error}</p>
        <button
          onClick={fetchAlerts}
          style={{
            padding: '10px 20px',
            backgroundColor: TappyBrandColors.primary.teal,
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: 500
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Page Header */}
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h2 style={{ fontSize: '24px', fontWeight: 600, color: '#1f2937', marginBottom: '8px' }}>
            Safety & Content Moderation
          </h2>
          <p style={{ fontSize: '14px', color: '#6b7280' }}>
            Monitor flagged content and ensure student safety
          </p>
        </div>
        <button
          onClick={fetchAlerts}
          style={{
            padding: '10px 20px',
            backgroundColor: '#f3f4f6',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            color: '#374151'
          }}
        >
          🔄 Refresh
        </button>
      </div>

      {/* Summary Stats */}
      {stats && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '16px',
          marginBottom: '24px'
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '12px',
            border: '2px solid',
            borderColor: '#dc2626',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontSize: '32px', fontWeight: 700, color: '#dc2626', marginBottom: '4px' }}>
              {stats.critical || 0}
            </div>
            <div style={{ fontSize: '13px', color: '#991b1b', fontWeight: 600, textTransform: 'uppercase' }}>Critical</div>
          </div>

          <div style={{
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '12px',
            border: '2px solid',
            borderColor: '#f59e0b',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontSize: '32px', fontWeight: 700, color: '#f59e0b', marginBottom: '4px' }}>
              {stats.high || 0}
            </div>
            <div style={{ fontSize: '13px', color: '#92400e', fontWeight: 600, textTransform: 'uppercase' }}>High</div>
          </div>

          <div style={{
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '12px',
            border: '2px solid',
            borderColor: '#fbbf24',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontSize: '32px', fontWeight: 700, color: '#fbbf24', marginBottom: '4px' }}>
              {stats.medium || 0}
            </div>
            <div style={{ fontSize: '13px', color: '#92400e', fontWeight: 600, textTransform: 'uppercase' }}>Medium</div>
          </div>

          <div style={{
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '12px',
            border: '1px solid #e5e7eb',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontSize: '32px', fontWeight: 700, color: '#6b7280', marginBottom: '4px' }}>
              {stats.low || 0}
            </div>
            <div style={{ fontSize: '13px', color: '#6b7280', fontWeight: 600, textTransform: 'uppercase' }}>Low</div>
          </div>

          <div style={{
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '12px',
            border: '1px solid #e5e7eb',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <div style={{ fontSize: '32px', fontWeight: 700, color: '#4F46E5', marginBottom: '4px' }}>
              {stats.total || 0}
            </div>
            <div style={{ fontSize: '13px', color: '#6b7280', fontWeight: 600, textTransform: 'uppercase' }}>Total Alerts</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div style={{
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        marginBottom: '20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
          {/* Severity Filter */}
          <select
            value={filters.severity}
            onChange={(e) => setFilters({ ...filters, severity: e.target.value })}
            style={{
              padding: '10px 14px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              outline: 'none',
              backgroundColor: 'white',
              cursor: 'pointer'
            }}
          >
            <option value="">All Severities</option>
            <option value="CRITICAL">Critical</option>
            <option value="HIGH">High</option>
            <option value="MEDIUM">Medium</option>
            <option value="LOW">Low</option>
          </select>

          {/* Status Filter */}
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            style={{
              padding: '10px 14px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              outline: 'none',
              backgroundColor: 'white',
              cursor: 'pointer'
            }}
          >
            <option value="">All Statuses</option>
            <option value="NEW">New (Needs Review)</option>
            <option value="REVIEWED">Reviewed</option>
            <option value="RESOLVED">Resolved</option>
            <option value="ESCALATED">Escalated</option>
          </select>

          {/* Time Period */}
          <select
            value={filters.period}
            onChange={(e) => setFilters({ ...filters, period: e.target.value })}
            style={{
              padding: '10px 14px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              outline: 'none',
              backgroundColor: 'white',
              cursor: 'pointer'
            }}
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="all">All time</option>
          </select>
        </div>
      </div>

      {/* Alerts List */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>⏳</div>
            <p style={{ color: '#6b7280' }}>Loading alerts...</p>
          </div>
        ) : alerts.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>✅</div>
            <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#1f2937', marginBottom: '8px' }}>
              No Alerts
            </h3>
            <p style={{ fontSize: '14px', color: '#6b7280' }}>
              {filters.severity || filters.status
                ? 'No alerts match your filters'
                : 'All clear! No content moderation alerts for this period.'}
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
            {alerts.map((alert, index) => {
              const severity = getSeverityColor(alert.severity);
              const status = getStatusColor(alert.status);
              return (
                <div
                  key={alert.id}
                  style={{
                    padding: '20px 24px',
                    borderBottom: index < alerts.length - 1 ? '1px solid #f3f4f6' : 'none',
                    borderLeft: `4px solid ${severity.border}`,
                    backgroundColor: '#ffffff',
                    transition: 'background-color 0.15s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ffffff'}
                >
                  {/* Alert Header */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
                        <span style={{
                          display: 'inline-block',
                          padding: '4px 10px',
                          backgroundColor: severity.bg,
                          color: severity.text,
                          borderRadius: '6px',
                          fontSize: '11px',
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em'
                        }}>
                          {alert.severity}
                        </span>
                        <span style={{
                          display: 'inline-block',
                          padding: '4px 10px',
                          backgroundColor: status.bg,
                          color: status.text,
                          borderRadius: '6px',
                          fontSize: '11px',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em'
                        }}>
                          {status.label}
                        </span>
                        {alert.category && (
                          <span style={{
                            fontSize: '12px',
                            color: '#6b7280',
                            padding: '4px 8px',
                            backgroundColor: '#f3f4f6',
                            borderRadius: '6px'
                          }}>
                            {alert.category.replace('_', ' ')}
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: '14px', color: '#1f2937', marginBottom: '4px' }}>
                        <strong>Student:</strong> {alert.student ? `${alert.student.first_name} ${alert.student.last_name}` : `ID ${alert.student_id}`}
                        {alert.student?.grade_level && <span style={{ color: '#6b7280', marginLeft: '8px' }}>({alert.student.grade_level})</span>}
                      </div>
                      {alert.teacher_name && (
                        <div style={{ fontSize: '13px', color: '#6b7280' }}>
                          <strong>Teacher:</strong> {alert.teacher_name} {alert.course_name ? `• ${alert.course_name}` : ''}
                        </div>
                      )}
                    </div>
                    <div style={{ textAlign: 'right', minWidth: '120px' }}>
                      <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                        {formatTimeAgo(alert.created_at)}
                      </div>
                    </div>
                  </div>

                  {/* Alert Content (Transcription) */}
                  {alert.transcription && (
                    <div style={{
                      padding: '12px 16px',
                      backgroundColor: '#fef3c7',
                      borderRadius: '6px',
                      marginBottom: '12px',
                      border: '1px solid #fbbf24'
                    }}>
                      <div style={{ fontSize: '12px', color: '#92400e', fontWeight: 600, marginBottom: '4px' }}>
                        Flagged Content:
                      </div>
                      <div style={{ fontSize: '13px', color: '#78350f', fontFamily: 'monospace' }}>
                        "{alert.transcription}"
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  {!alert.reviewed && (
                    <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                      <button
                        onClick={() => handleMarkReviewed(alert.id)}
                        style={{
                          padding: '8px 16px',
                          backgroundColor: TappyBrandColors.primary.teal,
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: '13px',
                          fontWeight: 500
                        }}
                      >
                        Mark Reviewed
                      </button>
                      <button
                        onClick={() => handleMarkFalsePositive(alert.id)}
                        style={{
                          padding: '8px 16px',
                          backgroundColor: '#f3f4f6',
                          color: '#374151',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '13px',
                          fontWeight: 500
                        }}
                      >
                        False Positive
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Info Box */}
      <div style={{
        marginTop: '24px',
        padding: '20px',
        backgroundColor: '#f0fdf4',
        borderRadius: '8px',
        border: '1px solid #22c55e'
      }}>
        <div style={{ display: 'flex', gap: '12px' }}>
          <div style={{
            width: '32px',
            height: '32px',
            borderRadius: '8px',
            backgroundColor: TappyBrandColors.semantic.success,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '16px',
            color: 'white',
            flexShrink: 0
          }}>i</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '14px', fontWeight: 600, color: TappyBrandColors.primary.darkTeal, marginBottom: '8px' }}>
              About Content Moderation
            </div>
            <div style={{ fontSize: '13px', color: '#166534', lineHeight: '1.6' }}>
              Our AI-powered content moderation system monitors student conversations in real-time to ensure safety.
              Alerts are automatically sent to relevant teachers via email. School admins can review all alerts here
              for oversight and compliance purposes. All data is FERPA/COPPA compliant.
            </div>
          </div>
        </div>
      </div>

      {/* Export Section - Tappy Brand Style */}
      <div style={{
        marginTop: '32px',
        padding: '20px',
        backgroundColor: TappyBrandColors.neutral.paleGray,
        border: `2px solid ${TappyBrandColors.primary.teal}`,
        borderLeft: `4px solid ${TappyBrandColors.primary.teal}`,
        borderRadius: '12px',
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        boxShadow: `0 4px 12px rgba(41, 122, 146, 0.15)`
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          borderRadius: '10px',
          backgroundColor: 'white',
          border: `2px solid ${TappyBrandColors.primary.teal}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}>
          <div style={{
            fontSize: '20px',
            fontWeight: 600,
            color: TappyBrandColors.primary.teal
          }}>📥</div>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{
            fontWeight: 600,
            color: TappyBrandColors.neutral.darkGray,
            marginBottom: '6px',
            fontSize: '15px'
          }}>
            Export Compliance Report
          </div>
          <div style={{
            fontSize: '13px',
            color: TappyBrandColors.primary.brandBlue,
            lineHeight: '1.5'
          }}>
            Download detailed safety moderation records for compliance and review
          </div>
        </div>
        <button
          onClick={async () => {
            try {
              const response = await axios.post(
                buildApiUrl('export/compliance-report'),
                {
                  filters: {
                    severity: filters.severity,
                    status: filters.status,
                    period: filters.period
                  }
                },
                { responseType: 'blob' }
              );
              const url = window.URL.createObjectURL(new Blob([response.data]));
              const link = document.createElement('a');
              link.href = url;
              link.setAttribute('download', response.headers['content-disposition']?.split('filename=')[1] || 'compliance_report.csv');
              document.body.appendChild(link);
              link.click();
              link.remove();
              window.URL.revokeObjectURL(url);
            } catch (err) {
              console.error('Export error:', err);
              alert('Failed to export compliance report. Please try again.');
            }
          }}
          style={{
            padding: '10px 20px',
            backgroundColor: TappyBrandColors.primary.teal,
            color: 'white',
            border: 'none',
            borderRadius: '10px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 600,
            flexShrink: 0,
            transition: 'all 200ms ease',
            boxShadow: `0 2px 8px rgba(41, 122, 146, 0.25)`
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = TappyBrandColors.primary.brightTeal;
            e.currentTarget.style.transform = 'translateY(-2px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = TappyBrandColors.primary.teal;
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          Download Report
        </button>
      </div>
    </div>
  );
}

export default SchoolSafetyDashboard;
